import { useEffect } from 'react';

import { MessageError } from 'config/types';
import { textConstants } from 'constants/texts';
import { useContactListFeatureQuery } from 'hooks/queries/useContactListFeatureQuery';
import { useOpenSearchQuery } from 'hooks/queries/useOpenSearchQuery';
import { useAmplitude } from 'hooks/useAmplitude';
import { AmplitudeData } from 'hooks/useAmplitude/types';
import { useOpenSearchContext } from 'hooks/useOpenSearchContext';
import { useSendTracking } from 'hooks/useSendTracking';
import { DataEvent } from 'hooks/useSendTracking/types';
import { ITruckerProfileProps } from 'interfaces/profile.interface';
import { clearMask, formatCPF, phoneMask } from 'utils';
import { isEmpty } from 'utils/validate';
import { useLastSearchedDrivers } from '../useLastSearchedDrivers';

export const useSearchTrucker = () => {
  const {
    valueInputSearch,
    inputHasError,
    isInputSearchMasked,
    setIsCardVisible,
    setIsEmpty,
    setIsNotActive,
    setIsDivergence,
    setIsLoadingRequest,
    setIsError,
    setRefetchSearch,
    setSearchTruckerData,
    setValueInputSearch,
  } = useOpenSearchContext();

  const getInputValueWithoutMask = () => {
    if (!isEmpty(valueInputSearch)) {
      return clearMask(valueInputSearch);
    }
    return valueInputSearch;
  };

  const whenEnableRequest = getInputValueWithoutMask()?.length === 11 && !inputHasError;

  const { updateSearchedDrivers } = useLastSearchedDrivers();
  const { sendAmplitude } = useAmplitude();
  const { sendTrackingJaiminho } = useSendTracking();

  const {
    data,
    isFetching,
    isError: reactQueryIsError,
    error: errorReactQuery,
    isFetched,
    refetch,
    isSuccess,
  } = useOpenSearchQuery(getInputValueWithoutMask(), whenEnableRequest);

  const typeOfSearched = (truckerData: ITruckerProfileProps) => {
    if (truckerData?.trucker?.cpf === getInputValueWithoutMask()) return 'cpf';
    if (!!truckerData) return 'telefone';
    return 'nao-disponivel';
  };

  const getAccountType = (truckerData: ITruckerProfileProps) => {
    if (!truckerData) return textConstants.notAvailaible;
    if (truckerData?.trucker?.bank?.pix?.length > 0) return 'Pix';
    if (truckerData?.trucker?.bank?.account?.number) return 'Ted';
    return 'sem-conta';
  };

  const dispatchAmplitudeEvent = (trackingData: AmplitudeData) => {
    sendAmplitude('trucker_listing_search_driver', trackingData);
  };

  const dispatchTrackingEvent = (trackingData: DataEvent) => {
    sendTrackingJaiminho(
      {
        nameEvent: 'trucker_listing_search_driver',
        data: trackingData,
      },
      'open_search'
    );
  };

  useEffect(() => {
    if (isFetched && isSuccess) {
      if (!isInputSearchMasked) {
        const isCpf = data.trucker.cpf === getInputValueWithoutMask();
        isCpf
          ? setValueInputSearch(formatCPF(getInputValueWithoutMask()))
          : setValueInputSearch(phoneMask(getInputValueWithoutMask()));
      }

      updateSearchedDrivers({
        id: data.trucker.id,
        isVerified: data.isVerified,
        isVip: data.trucker.isVip,
        name: data.trucker.name,
        areaCodes: Array.from(new Set(data?.trucker?.phones?.map((phone) => phone.ddd))),
        vehicle: {
          tractor: data.trucker.vehicles?.[0]?.tractor?.type,
          body: data.trucker.vehicles?.[0]?.body?.[0]?.name,
        },
      });

      const trackingData = {
        value: getInputValueWithoutMask(),
        freightCode: textConstants.notAvailaible,
        bankInfo: getAccountType(data),
        driver: data.trucker.id,
        type: typeOfSearched(data),
        source: 'home',
        isVIP: data?.trucker.isVip,
      };

      dispatchAmplitudeEvent(trackingData);
      dispatchTrackingEvent(trackingData);
    } else if (errorReactQuery) {
      if (errorReactQuery.message === MessageError.notActive) {
        const trackingData = {
          value: getInputValueWithoutMask(),
          freightCode: textConstants.notAvailaible,
          driver: 'motorista-bloqueado',
          source: 'home',
        };

        dispatchAmplitudeEvent(trackingData);
        dispatchTrackingEvent(trackingData);
      }

      if (errorReactQuery.message === MessageError.notFound) {
        const notAvailaibleTrucker = {
          value: getInputValueWithoutMask(),
          freightCode: textConstants.notAvailaible,
          bankInfo: getAccountType(null),
          driver: 'motorista-fora-base',
          type: typeOfSearched(null),
          source: 'home',
        };

        dispatchAmplitudeEvent(notAvailaibleTrucker);
        dispatchTrackingEvent(notAvailaibleTrucker);
      }
    }
  }, [data, isSuccess, errorReactQuery]);

  useContactListFeatureQuery({ enabled: whenEnableRequest });

  const emptyWithoutError = isFetched && !isFetching && errorReactQuery?.message === MessageError.notFound;
  const errorWithDuplicatedData = isFetched && !isFetching && errorReactQuery?.message === MessageError.duplicated;
  const errorIsNotActive = isFetched && !isFetching && errorReactQuery?.message === MessageError.notActive;
  const error = isFetched && !isFetching && reactQueryIsError;

  useEffect(() => {
    if (isFetching) {
      setIsEmpty(false);
      setIsDivergence(false);
      setIsError(false);
      setIsNotActive(false);
    }

    if ((data || errorReactQuery || isFetching || reactQueryIsError) && whenEnableRequest) {
      setIsCardVisible(true);
    } else {
      setIsCardVisible(false);
    }

    if (emptyWithoutError) {
      setIsEmpty(true);
    }

    if (errorIsNotActive) {
      setIsNotActive(true);
    }

    if (!errorWithDuplicatedData && !errorIsNotActive && error) {
      setIsError(true);
    }

    if (errorWithDuplicatedData) {
      setIsDivergence(true);
    }
  }, [data, isFetching, errorReactQuery, valueInputSearch, reactQueryIsError]);

  useEffect(() => {
    setSearchTruckerData(data);
  }, [data]);

  useEffect(() => {
    setIsLoadingRequest(isFetching);
  }, [isFetching]);

  useEffect(() => {
    setRefetchSearch(/* istanbul ignore next */ () => refetch);
  }, [refetch]);
};
